import { Link as TanStackLink, type LinkProps } from "@tanstack/react-router";
import type { AnchorHTMLAttributes, ReactNode, Ref } from "react";

import { cn } from "@/lib/utils";

/** Where a masked link points crawlers, the address bar and new tabs. */
export type LinkMask = {
  to: string;
  params?: Record<string, string>;
  unmaskOnReload?: boolean;
};

/**
 * Native link target — the router encodes `search` through the global codec, so
 * callers pass plain values and never hand-build a URL string.
 */
export type LinkTarget = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  mask?: LinkMask;
};

type Props = Omit<LinkProps, "to" | "params" | "search" | "mask"> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps | "href"> & {
    appearance?: "text" | "unstyled";
    children?: ReactNode;
    ref?: Ref<HTMLAnchorElement>;
  } & Partial<LinkTarget>;

/**
 * App wrapper over TanStack Router's `<Link>`. Pass native `to`/`params`/`search`
 * (a bare path goes in `to`) — the router encodes search params for us, so call
 * sites never hand-build a URL. The strict generic route typing is cast away
 * here, once, so call sites stay clean. `preload` maps to `defaultPreload` on
 * the router.
 */
export function Link({
  to,
  params,
  search,
  mask,
  appearance = "unstyled",
  children,
  className,
  ...rest
}: Props) {
  return (
    <TanStackLink
      to={to as string}
      params={params as never}
      search={search as never}
      mask={mask as never}
      className={cn(
        appearance === "text" &&
          "rounded-sm text-primary underline decoration-primary/50 underline-offset-4 transition-colors hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
      {...(rest as object)}
    >
      {children}
    </TanStackLink>
  );
}
